/**
 * Regroupement des défis live par campagne pour la console admin « Contenu des
 * défis ». Logique PURE (testée) : les défis sans campagne (`campaign_id` null,
 * défis manuels) tombent dans un groupe « Hors campagne » placé en dernier ; les
 * campagnes sont ordonnées par la date de leur défi le plus proche.
 */

export interface GroupableChallenge {
  campaign_id: string | null;
  campaignTitle: string | null;
  starts_at: string;
}

export interface CampaignGroup<T extends GroupableChallenge> {
  campaignId: string | null;
  title: string | null;
  rows: T[];
}

const NONE_KEY = '__none__';

export function groupChallengesByCampaign<T extends GroupableChallenge>(
  rows: T[],
): CampaignGroup<T>[] {
  const byKey = new Map<string, CampaignGroup<T>>();
  for (const row of rows) {
    const key = row.campaign_id ?? NONE_KEY;
    let group = byKey.get(key);
    if (!group) {
      group = { campaignId: row.campaign_id, title: row.campaignTitle, rows: [] };
      byKey.set(key, group);
    }
    // Le titre peut n'apparaître que sur certaines lignes : on garde le premier non vide.
    if (!group.title && row.campaignTitle) group.title = row.campaignTitle;
    group.rows.push(row);
  }

  const earliest = (g: CampaignGroup<T>): string =>
    g.rows.reduce<string | null>(
      (min, r) => (min === null || r.starts_at < min ? r.starts_at : min),
      null,
    ) ?? '';

  return [...byKey.values()].sort((a, b) => {
    // Groupe « Hors campagne » toujours en dernier.
    if (a.campaignId === null) return b.campaignId === null ? 0 : 1;
    if (b.campaignId === null) return -1;
    const ea = earliest(a);
    const eb = earliest(b);
    return ea < eb ? -1 : ea > eb ? 1 : 0;
  });
}
